import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);

/**
 * Reglas de tiempo del reporte, en un solo lugar.
 *
 * TODO lo que ve el usuario va en SU zona horaria, no en la de la compañía.
 * El contrato mezcla tres formatos y cada uno necesita un trato distinto:
 *
 * - `startedAt` / `finishedAt` (rutas y paradas) viajan en UTC (`…Z`).
 * - `context.windowStart` / `windowEnd` son `HH:mm` **en la zona de la
 *   compañía**, sin fecha: hay que anclarlos al día del reporte antes de poder
 *   convertirlos.
 * - `context.serverTime` ya viene convertido y con su offset.
 */

/** Zona del navegador. Es la referencia de todo lo que se muestra. */
export const getUserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || dayjs.tz.guess();
  } catch {
    return 'UTC';
  }
};

/**
 * Un `AAAA-MM-DD` escrito largo en el idioma activo.
 *
 * Va por el formato `LL` de dayjs y no por una plantilla propia porque la
 * preposición y el orden de los campos cambian con el idioma: un `D [de] MMMM`
 * fijo devuelve "13 de August" apenas la interfaz pasa a inglés.
 */
export const formatLongDate = (date: string): string =>
  dayjs(date).format('LL');

/** Un instante UTC del contrato, en `HH:mm` de la zona del usuario. */
export const formatTime = (
  iso: string | null | undefined,
  timeZone: string
): string | null => (iso ? dayjs(iso).tz(timeZone).format('HH:mm') : null);

/**
 * El DÍA de un instante del contrato, escrito largo en el idioma activo.
 *
 * Acompaña siempre a la hora en el detalle de una parada: una parada puede
 * cruzar la medianoche de la zona del usuario, y "00:12" solo no dice de qué
 * día es. La zona se aplica antes de formatear porque es justamente el cambio
 * de zona el que puede mover la fecha.
 */
export const formatDate = (
  iso: string | null | undefined,
  timeZone: string
): string | null => (iso ? dayjs(iso).tz(timeZone).format('LL') : null);

/**
 * Minutos entre dos instantes del contrato, o `null` si falta alguno.
 *
 * Devuelve el número y no el texto: cómo se escribe "1 h 6 min" es cosa de las
 * traducciones, no de la aritmética.
 */
export const getDurationMinutes = (
  from: string | null | undefined,
  to: string | null | undefined
): number | null =>
  from && to ? Math.round(dayjs(to).diff(dayjs(from), 'minute', true)) : null;

/**
 * Cualquier instante en la zona del usuario. Pasa por acá y no por `dayjs`
 * directo para no depender de que el plugin `timezone` ya esté extendido en el
 * archivo que llama.
 */
export const formatInstant = (
  value: Date | string,
  timeZone: string,
  format = 'HH:mm:ss'
): string => dayjs(value).tz(timeZone).format(format);

/**
 * La ventana del día, como dos INSTANTES absolutos.
 *
 * El eje se calculaba antes en "minutos desde medianoche", y esa unidad no
 * alcanza: descarta la fecha, así que una parada cerrada pasada la medianoche
 * queda indistinguible de una de la madrugada anterior y termina dibujada en el
 * extremo opuesto del track. Con instantes esa ambigüedad no existe y no hace
 * falta ningún ajuste por cruce de día.
 *
 * La zona del usuario no interviene acá: un instante es el mismo en todas las
 * zonas. Solo hace falta para ESCRIBIR la hora, en `getAxisTicks`.
 */
export interface TrackWindow {
  startMs: number;
  endMs: number;
}

export const getUserWindow = (
  date: string,
  windowStart: string,
  windowEnd: string,
  companyTimeZone: string
): TrackWindow => {
  const start = dayjs.tz(`${date} ${windowStart}`, companyTimeZone);
  const sameDayEnd = dayjs.tz(`${date} ${windowEnd}`, companyTimeZone);

  /**
   * Una ventana que termina antes de empezar es una que cierra al día
   * siguiente: 22:00–04:00 se ancla al mismo día y hay que correr el cierre.
   *
   * El día se avanza en la FECHA y se vuelve a anclar en la zona de la
   * compañía, en vez de sumarle 24 horas al instante. `dayjs.tz` devuelve una
   * instancia con el offset congelado, así que un `add(1, 'day')` sobre ella
   * suma un día de reloj en ese offset viejo: en una noche de cambio de horario
   * el cierre caía una hora corrido y toda la escala quedaba deformada.
   */
  const end = sameDayEnd.isAfter(start)
    ? sameDayEnd
    : dayjs.tz(
        `${dayjs(date).add(1, 'day').format('YYYY-MM-DD')} ${windowEnd}`,
        companyTimeZone
      );

  return { startMs: start.valueOf(), endMs: end.valueOf() };
};

/** Instante en milisegundos de un `startedAt` / `finishedAt` del contrato. */
export const toInstant = (iso: string): number => dayjs(iso).valueOf();

/**
 * Si el reporte es del día en curso, en la zona de la COMPAÑÍA.
 *
 * La marca "ahora" del timeline solo informa sobre un reporte vigente: sobre
 * un día cerrado hace horas quedaría clavada en el borde derecho diciendo una
 * hora que ya no tiene nada que ver con lo que se está mirando. La comparación
 * se hace en la zona de la COMPAÑÍA y no en la del navegador ni en UTC crudo:
 * `serverTime` cercano a medianoche puede caer en un día distinto según quién
 * lo mire, y el día que importa acá es el operativo del reporte, no el del
 * usuario que lo está leyendo.
 */
export const isReportToday = (
  date: string,
  serverTime: string,
  companyTimeZone: string
): boolean =>
  dayjs(serverTime).tz(companyTimeZone).format('YYYY-MM-DD') === date;

/**
 * Posición de un instante dentro de la ventana, en porcentaje.
 *
 * Se recorta a 0–100: una parada ejecutada fuera de la ventana declarada existe
 * —una ruta que arrancó antes de hora, o una que cerró pasado el horario— y
 * debe verse en el borde que le corresponde, no en el opuesto ni fuera del
 * track. Al comparar instantes, "antes" y "después" son inequívocos.
 */
export const getTrackPosition = (
  instantMs: number,
  window: TrackWindow
): number => {
  const span = window.endMs - window.startMs;
  if (span <= 0) return 0;

  return Math.min(
    100,
    Math.max(0, ((instantMs - window.startMs) / span) * 100)
  );
};

/**
 * Marcas del eje, cada `stepMinutes` **desde el inicio de la ventana**.
 *
 * Arrancan en `windowStart` y no en múltiplos absolutos de la hora: si no, una
 * ventana desplazada por zona horaria abre y cierra en horas que no son sus
 * bordes reales. La etiqueta se escribe en la zona del USUARIO, que es donde
 * se lee.
 */
export const getAxisTicks = (
  window: TrackWindow,
  userTimeZone: string,
  stepMinutes = 120
): { instantMs: number; label: string; position: number }[] => {
  const ticks = [];
  const step = stepMinutes * 60_000;

  for (let ms = window.startMs; ms <= window.endMs; ms += step) {
    ticks.push({
      instantMs: ms,
      label: dayjs(ms).tz(userTimeZone).format('HH:mm'),
      position: getTrackPosition(ms, window),
    });
  }

  return ticks;
};
